// react
import { SyntheticEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// types
import { Account, ActionButton } from '../../../@types';

// router
import {
	Link as RouterLink,
	Outlet,
	useLocation,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextProject, ContextUserProjectPermissions } from '../Project/Project.context';
import { ContextUser } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Grid } from '@mui/material';

// pages
import { Details } from './Details/Details';

// ui
import { Breadcrumbs } from '../../ui/Breadcrumbs/Breadcrumbs';
import { Empty } from '../../ui/Empty/Empty';
import { IconCircleProps } from '../../ui/IconCircle/IconCircle';
import { Loading } from '../../ui/Loading/Loading';
import { Tabs } from '../../ui/Tabs/Tabs';
import { Title } from '../../ui/Title/Title';

// dialogs
import { DialogAddAccountBalance } from '../../dialogs/DialogAddAccountBalance/DialogAddAccountBalance';
import { DialogAddComment } from '../../dialogs/DialogAddComment/DialogAddComment';
import { DialogEditAccount } from '../../dialogs/DialogEditAccount/DialogEditAccount';
import { DialogUpdateAccountStatus } from '../../dialogs/DialogUpdateAccountStatus/DialogUpdateAccountStatus';

export const PageAccount = () => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { id, idAccount } = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogAccount = searchParams.get('dialogAccount');
	const paramIsOnChain = searchParams.get('isOnChain');

	// context
	const { project } = useContext(ContextProject);
	const { user } = useContext(ContextUser);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// check if user has permissions to read transactions
	const hasTransactionsReadPermissions =
		userProjectPermissions?.cashBenefit?.read ||
		userProjectPermissions?.nonCashBenefit?.read ||
		userProjectPermissions?.opEx?.read ||
		userProjectPermissions?.payment?.read ||
		userProjectPermissions?.replenishment?.read ||
		userProjectPermissions?.transfer?.read;

	// defaults
	const tabs = useMemo(() => {
		const tabs = [];

		if (hasTransactionsReadPermissions) {
			tabs.push({
				component: RouterLink,
				label: t('activity'),
				to: `/project/${id}/account/${idAccount}/activity`,
				value: 'activity',
			});
		}

		if (userProjectPermissions?.comment?.read) {
			tabs.push({
				component: RouterLink,
				label: t('comments'),
				to: `/project/${id}/account/${idAccount}/comments`,
				value: 'comments',
			});
		}

		return tabs;
	}, [hasTransactionsReadPermissions, id, idAccount, t, userProjectPermissions]);

	// defaults
	const getTabByLocation = useCallback(
		(pathname) => {
			if (pathname.includes('activity')) {
				return 'activity';
			} else if (pathname.includes('comments')) {
				return 'comments';
			} else {
				return tabs?.[0]?.value;
			}
		},
		[tabs]
	);

	const defaultTabValue = useMemo(() => {
		return getTabByLocation(location.pathname);
	}, [getTabByLocation, location]);

	// state
	const [account, setAccount] = useState<Account | null>(null);
	const [tabValue, setTabValue] = useState(defaultTabValue);

	// fetch account
	let urlFetchAccount = `${process.env.REACT_APP_API_URL}/accounts/${idAccount}?populate=entities,loans`;

	if (paramIsOnChain) {
		urlFetchAccount += `&isOnChain=${paramIsOnChain}`;
	}

	const {
		fetchRequest: fetchAccount,
		isLoading: isLoadingAccount,
		refetch: refetchAccounts,
	} = useFetch({
		isLazy: true,
		url: urlFetchAccount,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setAccount(res.data[0]);
			}
		},
	});

	// handlers
	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogAccount');
		setSearchParams(searchParams);
	};

	const titleActions: ActionButton[] = [];

	if (
		account &&
		(userProjectPermissions.account.update || userProjectPermissions?.replenishment.create)
	) {
		const actions: ActionButton[] = [];

		if (
			tabValue === 'activity' &&
			userProjectPermissions?.replenishment?.create &&
			(user?.role === 'WFA' || account.type === 'LOCAL')
		) {
			actions.push({
				children: t('addToBalance'),
				onClick: () => {
					searchParams.set('dialog', 'addAccountBalance');
					searchParams.set('dialogAccount', String(account.id));
					setSearchParams(searchParams);
				},
				variant: 'contained',
			});
		}

		if (tabValue === 'activity' && userProjectPermissions?.account?.update) {
			actions.push(
				{
					children: t('editAccount'),
					onClick: () => {
						searchParams.set('dialog', 'editAccount');
						searchParams.set('dialogAccount', String(account.id));
						setSearchParams(searchParams);
					},
					variant: 'contained',
				},
				{
					children: account.status === 'DEACTIVATED' ? t('activate') : t('deactivate'),
					onClick: () => {
						searchParams.set('dialog', 'updateAccountStatus');
						searchParams.set('dialogAccount', String(account.id));
						setSearchParams(searchParams);
					},
					variant: 'contained',
				}
			);
		}
		if (tabValue === 'comments' && userProjectPermissions?.comment?.create) {
			titleActions.push({
				children: t('addComment'),
				onClick: () => {
					searchParams.set('dialog', 'addComment');
					setSearchParams(searchParams);
				},
				variant: 'contained',
			});
		}

		if (actions.length) {
			titleActions.push({
				BtnProps: {
					actions,
				},
			});
		}
	}

	// fetch account when idAccount changes
	useEffect(() => {
		if (idAccount) {
			fetchAccount();
		}
	}, [fetchAccount, idAccount]);

	// set active tab when location changes
	useEffect(() => {
		const tabValueByLocation = getTabByLocation(location.pathname);
		setTabValue(tabValueByLocation);
	}, [getTabByLocation, location]);

	// navigate to first tab if path is not set
	useEffect(() => {
		const segments = location.pathname.split('/');
		if (segments.length < 6) {
			navigate(tabs[0].to);
		}
	}, [location, navigate, tabs]);

	const renderContent = () => {
		if (isLoadingAccount) {
			return <Loading variant="page" />;
		}

		if (account) {
			const IconCircleProps: IconCircleProps = {
				type: 'account',
			};

			let overline = t('designatedAccount');

			if (account.type === 'LOCAL') {
				IconCircleProps.type = 'local';
				overline = t('localAccount');
			}

			if (account.isReimbursement) {
				IconCircleProps.color = 'warning';
				IconCircleProps.type = 'reimbursement';
				overline = t('reimbursementAccount');
			}
			return (
				<>
					<Grid container spacing={2}>
						<Grid item xs={11} lg={12}>
							<Breadcrumbs
								crumbs={[
									{
										children: t('projects'),
										to: '/projects',
									},
									{
										children: project?.idWbg,
										to: `/project/${id}`,
									},
									{
										children: t('accounts'),
										to: `/project/${id}/accounts`,
									},
								]}
								page={account.name}
							/>
						</Grid>
						<Grid item xs={12}>
							<Title
								actions={titleActions}
								ChipStatusProps={{
									status: account.status,
								}}
								color="blue"
								IconCircleProps={IconCircleProps}
								overline={overline}
								title={account.name}
							/>
						</Grid>
						<Grid item xs={12}>
							<Details account={account} />
						</Grid>
						<Grid item xs={12}>
							<Tabs onChange={handleChangeTab} tabs={tabs} value={tabValue} hasBorder={false} />
						</Grid>
						<Grid item xs={12}>
							<Outlet />
						</Grid>
					</Grid>
					<DialogAddAccountBalance
						account={account}
						isOpen={
							paramDialog === 'addAccountBalance' && paramDialogAccount === String(account.id)
						}
						onClose={handleCloseDialog}
						onSuccess={() => {
							refetchAccounts();
						}}
					/>
					<DialogEditAccount
						isOpen={paramDialog === 'editAccount' && paramDialogAccount === String(account.id)}
						onClose={handleCloseDialog}
						onSuccess={() => {
							refetchAccounts();
						}}
					/>
					<DialogUpdateAccountStatus
						account={account}
						isOpen={
							paramDialog === 'updateAccountStatus' && paramDialogAccount === String(account.id)
						}
						onClose={handleCloseDialog}
						onSuccess={() => {
							refetchAccounts();
						}}
					/>
					<DialogAddComment
						isOpen={paramDialog === 'addComment'}
						onClose={handleCloseDialog}
						onSuccess={() => {
							refetchAccounts();
						}}
					/>
				</>
			);
		}

		return (
			<Empty
				actions={[
					{
						children: t('accounts'),
						component: RouterLink,
						to: `/project/${project?.id}/accounts`,
						variant: 'contained',
					},
				]}
				content={{ children: t('empty.account.content') }}
				title={{ children: t('empty.account.title') }}
				IconCircleProps={{ type: 'account' }}
				variant="page"
			/>
		);
	};

	return <>{renderContent()}</>;
};
